import React, { FC, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { CatButton, CatSkeleton } from 'components/blocks-components';
import { Conditional, Portal } from '@cat-ecom/pcc-components';
import { useProductDetailPageContext } from '../../../context/product-detail';
import { subTotalResponse } from 'services/subTotal/types';
import { CurrentItems } from 'services/fbtItems/types';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import { getFBTSubTotal, addAllToCart } from 'services/fbtItems/actions';
import {
  checkIsNotWON,
  checkIsRiyal,
  getRiyalFormattedPrice,
  removeDecimalsForWonCurrency
} from 'app/[lang]/[esite]/utils/paymentUtils';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import {
  getFormattedItemData,
  getFormattedSearchSelectItem
} from 'components/product-comparison-grid/product-comparison-card/utils';
import {
  DEBOUNCE_TIME_SECONDS,
  FBT_LIST_NAME
} from '../../../constants/constants';
import { useQuickToast } from 'hooks/use-toast';
import cx from 'classnames';
import styles from './fbt-subtotal.module.scss';
import { useFbtMobileDrawer } from '../fbt-mobile-drawer/fbt-mobile-drawer-context';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { useCoreContext } from 'providers/core-provider';

interface FbtSubtotalActionProps {
  partsPriceData: ProductDealerPriceAvailability[];
  currentItem: CurrentItems;
  currency: string;
  subTotal: subTotalResponse;
  setSubTotal: (arg: {
    formattedSubtotal: string;
    unformattedSubtotal: number;
  }) => void;
  setTotalLoading: (arg: boolean) => void;
  totalLoading?: boolean;
  isFrequentlyBoughtMobile?: boolean;
  isCatCorp?: boolean;
  contactDealerComp?: React.ReactNode;
  itemSummary: ISummary;
  storeLocationId: string;
  cartId: string;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
}

const FbtSubtotalAction: FC<FbtSubtotalActionProps> = ({
  partsPriceData,
  subTotal,
  currency,
  currentItem,
  setSubTotal,
  setTotalLoading,
  totalLoading = false,
  isFrequentlyBoughtMobile,
  isCatCorp = false,
  contactDealerComp,
  itemSummary,
  storeLocationId,
  cartId,
  isRTL = false,
  currencyLdFeatureFlag = false
}) => {
  const t = useTranslations();
  const { closeDrawer } = useFbtMobileDrawer();
  const toast = useQuickToast();
  const { openDrawer } = useDealerLocatorDrawer();
  const { quantity } = useProductDetailPageContext();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { broadcastCartUpdate } = useCoreContext();
  const isRiyal = currencyLdFeatureFlag && checkIsRiyal(currency);
  useEffect(() => {
    const partsQtyPriceData = partsPriceData?.map((item) => {
      return {
        ...item,
        quantity:
          item.partNumber === currentItem.partNumber ? quantity : item.quantity
      };
    });
    setTotalLoading(true);

    (async () => {
      const { subTotalResponse, isLoading } = await getFBTSubTotal({
        currency: currency,
        fbtParts: partsQtyPriceData
      });
      setSubTotal(subTotalResponse);
      setTotalLoading(isLoading);
    })();
  }, [
    quantity,
    currency,
    currentItem.partNumber,
    partsPriceData,
    setSubTotal,
    setTotalLoading
  ]);

  const isTotalPriceSectionVisible = partsPriceData?.filter(
    (item) => item.enableAddToCart
  )?.length
    ? true
    : false;

  const isTotalPriceEnabled = partsPriceData?.filter(
    (item) => item.isChecked
  ).length;

  const handleAddToCartForMobile = () => {
    handleAddToCart(true);
  };

  const priceDisplayFormat = (price, currency = '', spanClassFbt = false) => {
    const isNotWON = checkIsNotWON(price, currency);
    const showPrice = () => {
      return isRiyal ? (
        getRiyalFormattedPrice({
          price: price,
          priceWrapperClasses: cx('px-1', { [styles['price']]: spanClassFbt }),
          isRTL: isRTL
        })
      ) : (
        <div className="self-align-bottom">
          <span className={cx({ [styles['price']]: spanClassFbt })}>
            {price}
          </span>
          <span
            className={cx({ 'cat-u-theme-typography-label ': spanClassFbt })}
          >
            {currency}
          </span>
        </div>
      );
    };
    return (
      <>
        {isNotWON ? (
          showPrice()
        ) : (
          <div>{`${removeDecimalsForWonCurrency(price)}`}</div>
        )}
      </>
    );
  };

  const handleAddToCart = async (fbtMobile = false) => {
    const partsQtyPriceData = partsPriceData?.map((item) => {
      return {
        ...item,
        quantity:
          item.partNumber === currentItem.partNumber ? quantity : item.quantity
      };
    });
    const result = await addAllToCart(partsQtyPriceData);
    if (result && !result.error) {
      broadcastCartUpdate?.();
      toast.success({ message: t('ITEM_ADDED_TO_CART') });

      const getPartPrice = (partNumber) => {
        const pricedata = partsPriceData?.filter((item) => {
          return partNumber === item.partNumber;
        });
        return pricedata?.length > 0 && pricedata[0];
      };

      const selectedItems = partsPriceData?.filter((item) => item?.isChecked);
        const partData = selectedItems?.map((item) => item.partNumber);
        fireSelectItemEvent({
          item: getFormattedSearchSelectItem(
            { orderItemId: cartId },
            getPartPrice(partData)
          ),
          partName: '',
          locationId: storeLocationId,
          itemSummary: selectedItems.map((item) => {
            const productData = itemSummary?.products?.find(
              (p) => p.partNumber === item.partNumber
            );
            return {
              ...item,
              manufacturer: productData?.manufacturer ?? 'Cat',
              categoryPath: item?.categoryPath,
              index: itemSummary?.products?.findIndex(
                (p) => p?.partNumber === item?.partNumber
              ),
              name: productData?.name
            };
          }),
          isSearchPageEvent: true,
          itemListId: FBT_LIST_NAME,
          itemListName: FBT_LIST_NAME,
          serialNumber: '',
          fitment: ''
        });
      if (fbtMobile) {
        setTimeout(() => {
          closeDrawer();
        }, DEBOUNCE_TIME_SECONDS);
      }

      addToCartItemEvent({
        locationId: storeLocationId,
        itemSummary: selectedItems.map((item) => ({
          ...item,
          manufacturer:
            itemSummary?.products?.find((p) => p.partNumber === item.partNumber)
              .manufacturer ?? 'Cat',
          index: itemSummary.products?.findIndex(
            (p) => p?.partNumber === item?.partNumber
          )
        })),
        itemData: selectedItems.flatMap((item) =>
          getFormattedItemData(cartId, item, currency)
        ),
        itemListId: FBT_LIST_NAME,
        itemListName: FBT_LIST_NAME
      });
    } else {
      toast.error({ message: t('ITEM_ADD_ERROR') });
    }
  };
  return (
    <>
      <>
        {isTotalPriceSectionVisible && (
          <div
            className={cx(
              'd-sm-flex justify-content-md-end justify-content-center py-2',
              { 'd-none d-md-block': isFrequentlyBoughtMobile }
            )}
          >
            {subTotal.unformattedSubtotal > 0 && (
              <Conditional
                test={!totalLoading}
                fallback={
                  <CatSkeleton
                    data-testid="loader-id"
                    size="lg"
                    className={`d-flex justify-content-center align-self-center ${styles['subtotal-loader']}`}
                  />
                }
              >
                <div className="d-flex cat-u-theme-typography-title-sm justify-content-center pe-3 pb-2">
                  <div className="align-self-center pe-1">
                    {`${t('TOTAL_PRICE')}:`}
                  </div>
                  {isRiyal
                    ? getRiyalFormattedPrice({
                        price: subTotal?.formattedSubtotal,
                        priceWrapperClasses: 'px-1',
                        isRTL: isRTL,
                        size: 'sm'
                      })
                    : priceDisplayFormat(subTotal?.formattedSubtotal, currency)}
                </div>
              </Conditional>
            )}

            <div>
              <CatButton
                size="sm"
                className="text-capitalize"
                variant="primary"
                fullWidth
                onClick={() => handleAddToCart()}
                disabled={!isTotalPriceEnabled}
              >
                {t('PM_ADD_ALL_TO_CART')}
              </CatButton>
            </div>
          </div>
        )}
      </>
      <div className={cx({ 'd-md-none': isFrequentlyBoughtMobile })}>
        <Portal id="fbt-mobile-drawer-footer-container">
          <Conditional test={isCatCorp}>
            <CatButton
              variant="contrast"
              size="md"
              onClick={openDrawer}
              fullWidth
            >
              {t('CAT_CHECK_PRICE')}
            </CatButton>
          </Conditional>
          <Conditional
            test={isTotalPriceSectionVisible}
            fallback={!isCatCorp && contactDealerComp}
          >
            <div
              className={`d-sm-flex flex-column justify-content-md-end justify-content-center py-2`}
            >
              <Conditional test={!!isTotalPriceEnabled}>
                <div className="d-flex cat-u-theme-typography-title-sm justify-content-between  pb-2">
                  <div className="align-self-center pe-1">{`${t('TOTAL_PRICE')}:`}</div>
                  {isRiyal
                    ? getRiyalFormattedPrice({
                        price: subTotal?.formattedSubtotal,
                        priceWrapperClasses: 'px-1',
                        isRTL: isRTL,
                        size: 'sm'
                      })
                    : priceDisplayFormat(
                        subTotal?.formattedSubtotal,
                        currency,
                        true
                      )}
                </div>
              </Conditional>
              <div>
                <CatButton
                  size="md"
                  className="text-capitalize"
                  variant="primary"
                  fullWidth
                  onClick={handleAddToCartForMobile}
                  disabled={!isTotalPriceEnabled}
                >
                  {t('PM_ADD_ALL_TO_CART')}
                </CatButton>
              </div>
            </div>
          </Conditional>
        </Portal>
      </div>
    </>
  );
};

export default FbtSubtotalAction;
